import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton, NioIcon } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateCategory, translateServiceLabel } from '../../../i18n/translateService';

function SubServiceDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t, language } = useTranslation();

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const subServiceCover = location.state?.subServiceCover || '/images/blog/a.jpg';
  const parentCategoryId = location.state?.parentCategoryId;
  const subServiceDescription = location.state?.subServiceDescription;
  const subServiceItems = location.state?.subServiceItems;
  const subServiceLabel = location.state?.subServiceLabel;

  const displayedSubService = useMemo(
    () => subServiceLabel || translateServiceLabel(t, selectedSubService),
    [t, selectedSubService, subServiceLabel],
  );

  const parentDisplayName = useMemo(() => {
    if (parentCategoryId) return translateCategory(t, parentCategoryId);
    const raw = location.state?.parentServiceTitle;
    if (raw) return translateServiceLabel(t, raw);
    return t('common.service');
  }, [t, parentCategoryId, location.state?.parentServiceTitle]);

  const descriptionText = useMemo(() => {
    if (subServiceDescription) {
      const text = subServiceDescription[language] || subServiceDescription.fr || subServiceDescription.en;
      if (text) return text;
    }
    return t('subService.intro', { parent: parentDisplayName });
  }, [subServiceDescription, language, t, parentDisplayName]);

  const benefitItems = useMemo(() => {
    if (subServiceItems && Array.isArray(subServiceItems) && subServiceItems.length > 0) {
      return subServiceItems
        .map(item => item[language] || item.fr || item.en)
        .filter(Boolean);
    }
    return [t('subService.benefit1'), t('subService.benefit2'), t('subService.benefit3')];
  }, [subServiceItems, language, t]);

  const canonicalParentTitle = location.state?.parentServiceTitle || t('common.service');
  return (
    <AppLayout title={t('subService.pageTitle', { name: displayedSubService })} rootClass="layout-1">
      <style>{`
        .subservice-details-page {
          min-height: 75vh;
          background: #f3f4f6;
          border-radius: 16px;
        }
        .subservice-details-card {
          background: transparent;
          border: 0;
          box-shadow: none;
        }
        .subservice-details-cover {
          width: 100%;
          max-width: 420px;
          aspect-ratio: 1.15 / 1;
          border-radius: 12px;
          object-fit: cover;
          display: block;
        }
        .subservice-details-title {
          color: #0f2b3d;
          font-weight: 700;
          font-size: clamp(1.3rem, 2vw, 1.9rem);
          margin-bottom: 1rem;
        }
        .subservice-details-text {
          color: #5b6570;
          line-height: 1.8;
          margin-bottom: 1.5rem;
          max-width: 760px;
        }
        .subservice-benefits li {
          color: #0f2b3d;
          margin-bottom: 0.85rem;
        }
        .subservice-benefits i {
          color: #0d6efd;
          margin-right: 0.5rem;
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-5">
        <NioSection.Content>
          <div className="subservice-details-page p-4 p-md-5">
            <div className="subservice-details-card">
              <Row className="align-items-center g-4 g-lg-5">
                <Col lg={5} className="text-center text-lg-start">
                  <img
                    src={subServiceCover}
                    alt={displayedSubService}
                    className="subservice-details-cover mx-auto mx-lg-0"
                  />
                </Col>
                <Col lg={7}>
                  <h2 className="subservice-details-title">{displayedSubService}</h2>
                  <p className="subservice-details-text">
                    {descriptionText}
                  </p>
                  <ul className="list-unstyled subservice-benefits mb-0">
                    {benefitItems.map((item, idx) => (
                      <li key={idx}><NioIcon name="check-circle" />{item}</li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-4">
            <NioButton
              className="btn btn-outline-primary"
              label={t('common.changeService')}
              onClick={() => navigate(-1)}
            />
            <Link
              to={`/service/${serviceId}/request-project-title`}
              state={{
                selectedSubService,
                parentServiceTitle: canonicalParentTitle,
                parentCategoryId,
              }}
            >
              <NioButton className="btn btn-primary" label={t('common.start')} />
            </Link>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default SubServiceDetails;
